import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { freeShippingProgress } from '@theme/eme-free-shipping-core';

const AMOUNT_MARKER = '__EME_AMOUNT__';

class EmeFreeShippingBar extends Component {
  #section = null;
  #quantity = 1;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.mode !== 'product') return;
    this.#section = this.closest('.shopify-section, dialog');
    if (!this.#section) return;
    this.#quantity = this.#readQuantity();
    this.#section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantity);
    this.#section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantity);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
  }

  #readQuantity() {
    const input = this.#section?.querySelector('quantity-selector-component input[name="quantity"]');
    const quantity = Number.parseInt(input?.value ?? '', 10);
    return quantity > 0 ? quantity : 1;
  }

  #handleQuantity = (event) => {
    if (event.detail?.cartLine || !(event.target instanceof Element) || event.target.closest('product-card')) return;
    const quantity = Number(event.detail?.quantity);
    if (!(quantity >= 0)) return;
    this.#quantity = quantity;
    this.#render();
  };

  #handleProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;
      const selector = this.dataset.blockId
        ? `eme-free-shipping-bar[data-block-id="${this.dataset.blockId}"]`
        : 'eme-free-shipping-bar[data-mode="product"]';
      const next = detail.html.querySelector(selector);
      if (!next?.dataset.unitPrice) return;
      this.dataset.unitPrice = next.dataset.unitPrice;
      this.#render();
    } catch (error) {
      if (error?.name !== 'AbortError') throw error;
    }
  };

  #render() {
    const { track, fill, text, remainingTemplate, reachedTemplate } = this.refs;
    if (!track || !fill || !text) return;
    const unitPrice = Number(this.dataset.unitPrice) || 0;
    const threshold = Number(this.dataset.threshold) || 0;
    const progress = freeShippingProgress(unitPrice * this.#quantity, threshold);
    this.hidden = progress === null;
    if (!progress) return;
    const { remainingCents, percent, reached } = progress;

    fill.style.setProperty('--eme-free-shipping-percent', `${percent}%`);
    track.setAttribute('aria-valuenow', String(percent));

    const template = reached ? reachedTemplate : remainingTemplate;
    if (!(template instanceof HTMLTemplateElement)) return;
    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const amount = formatMoney(remainingCents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (node.nodeValue?.includes(AMOUNT_MARKER)) node.nodeValue = node.nodeValue.replaceAll(AMOUNT_MARKER, amount);
    }
    text.replaceChildren(content);
  }
}

if (!customElements.get('eme-free-shipping-bar')) {
  customElements.define('eme-free-shipping-bar', EmeFreeShippingBar);
}
